// A single date range (month / range) shared across the Suppliers page and the
// Payments-tracking screen, so navigating between them keeps the same period:
// look at June on /suppliers, open /suppliers/paid, and June is already selected.
//
// Stored per user AND restaurant in sessionStorage (survives in-tab navigation
// and reloads, not a fresh session). Scoped by user id so swapping users in the
// same tab doesn't leak one user's period onto another, and by restaurant so a
// period picked for one restaurant doesn't follow the user into the next — each
// restaurant remembers its own (null restaurant = the "all restaurants" view).
// Only these two screens read/write it; the other date-filtered pages keep
// their own independent ranges.
//
// A preset is stored as its key and re-evaluated on read (period.js,
// toStoredPeriod), so "חודש נוכחי" picked in September reads as October once
// October comes — it used to come back as September for as long as the tab
// lived. The key was renamed with that change so a value written in the old
// dates-only shape is simply never read (→ the page default) rather than
// mistaken for a hand-picked range.
import { PRESETS_MONTHLY, toStoredPeriod, fromStoredPeriod } from './period';

const key = (uid, rid) => `peak.sharedPeriod.${uid ?? 'anon'}.${rid ?? 'all'}`;

// Returns { from, to } (ISO YYYY-MM-DD strings) or null when nothing is stored.
export function readSharedDateRange(uid, rid) {
  try {
    const r = fromStoredPeriod(JSON.parse(sessionStorage.getItem(key(uid, rid))), PRESETS_MONTHLY);
    return r && r.from && r.to ? r : null;
  } catch {
    return null;
  }
}

// `presetKey` comes from the page's usePresetPeriod — never re-derived here
// (see toStoredPeriod for why).
export function writeSharedDateRange(uid, rid, presetKey, from, to) {
  try {
    sessionStorage.setItem(key(uid, rid), JSON.stringify(toStoredPeriod(presetKey, from, to)));
  } catch (err) {
    // Storage disabled or full — non-fatal, the two screens just won't sync.
    console.warn('shared period not saved', err);
  }
}
